import { useEffect, useState } from 'react'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import type { DateFormat, SubscriptionPlan, UserSettings } from '../types'
import { DATE_FORMAT_LABELS, formatDate } from '../utils/dateFormat'
import { PageLayout } from './PageLayout'

type Props = {
  settings: UserSettings
  isSaving: boolean
  isLoading: boolean
  error: string | null
  onSave: (next: UserSettings) => Promise<void> | void
  userEmail: string | null
  onDeleteAccount: (email: string, reason: string, details?: string) => Promise<void> | void
  plan: SubscriptionPlan
}

type SwitchProps = {
  checked: boolean
  onChange?: (next: boolean) => void
  disabled?: boolean
}

const Switch = ({ checked, onChange, disabled }: SwitchProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange?.(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-app-line transition-colors duration-200 ${
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
    } ${checked ? 'bg-app-primary' : 'bg-app-subtle'}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-app-surface shadow transition duration-200 ${
        checked ? 'translate-x-5' : 'translate-x-1'
      }`}
    />
  </button>
)

export const SettingsPage = ({
  settings,
  isSaving,
  isLoading,
  error,
  onSave,
  userEmail,
  onDeleteAccount,
}: Props) => {
  const [localSettings, setLocalSettings] = useState<UserSettings>(settings)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteDetails, setDeleteDetails] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    setLocalSettings(settings)
  }, [settings])

  useEffect(() => {
    if (!isDeleteOpen) {
      setConfirmEmail('')
      setDeleteReason('')
      setDeleteDetails('')
      setDeleteError(null)
      setIsDeleting(false)
    }
  }, [isDeleteOpen])

  const saveNext = async (next: UserSettings) => {
    setLocalSettings(next)
    try {
      await Promise.resolve(onSave(next))
    } catch (err) {
      console.error(err)
    }
  }

  const expectedEmail = userEmail
  const canDelete =
    !!expectedEmail && confirmEmail.trim().toLowerCase() === expectedEmail.trim().toLowerCase() && deleteReason !== '' && !isDeleting

  return (
    <PageLayout
      icon={Cog6ToothIcon}
      title="Settings"
      subtitle="Manage your account preferences"
    >
      <div className="card p-6 space-y-6">
          <div className="space-y-4">

            {/* Date Format */}
            <div className="border border-app-line bg-app-page/60 p-3 rounded-2xl shadow-app-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-app-ink">Date format</p>
                  <p className="text-sm text-app-muted">
                    Preview: {formatDate(new Date().toISOString(), localSettings.dateFormat)}
                  </p>
                </div>
                <select
                  value={localSettings.dateFormat}
                  onChange={(e) => {
                    void saveNext({ ...localSettings, dateFormat: e.target.value as DateFormat })
                  }}
                  disabled={isSaving || isLoading}
                  className="rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none shrink-0"
                >
                  {(Object.keys(DATE_FORMAT_LABELS) as DateFormat[]).map((format) => (
                    <option key={format} value={format}>
                      {DATE_FORMAT_LABELS[format]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="border border-app-line bg-app-page/60 p-3 opacity-70 rounded-2xl shadow-app-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-app-ink">
                    Email integration{' '}
                    <span className="ml-2 rounded-full bg-app-line px-2 py-0.5 text-xs text-app-ink">Coming soon</span>
                  </p>
                  <p className="text-sm text-app-muted">
                    Connect your inbox to automatically track recruiter replies. This feature is in progress.
                  </p>
                </div>
                <div className="inline-flex items-center gap-3">
                  <span className="text-sm text-app-muted">Off</span>
                  <Switch checked={false} disabled />
                  <span className="text-sm text-app-muted">On</span>
                </div>
              </div>
            </div>

            <div className="border border-app-line bg-app-page/60 p-3 rounded-2xl shadow-app-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-app-ink">Auto-archive after 90 days</p>
                  <p className="text-sm text-app-muted">
                    Automatically archive applications with no activity for 90 days.
                    Applies only to active applications (not already archived). Runs daily via cron job.
                  </p>
                </div>
                <div className="inline-flex items-center gap-3">
                  <span className="text-sm text-app-muted">Off</span>
                  <Switch
                    checked={localSettings.autoArchiveEnabled}
                    onChange={(next) => {
                      void saveNext({ ...localSettings, autoArchiveEnabled: next })
                    }}
                    disabled={isSaving || isLoading}
                  />
                  <span className="text-sm text-app-muted">On</span>
                </div>
              </div>
            </div>

            <div className="border border-app-line bg-app-page/60 p-3 rounded-2xl shadow-app-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-app-ink">Auto-ghost after 30 days</p>
                  <p className="text-sm text-app-muted">
                    Automatically change status to "Ghosted" for applications with no response after 30 days.
                    Applies only to "Applied" and "In progress" statuses. Runs daily via cron job.
                  </p>
                </div>
                <div className="inline-flex items-center gap-3">
                  <span className="text-sm text-app-muted">Off</span>
                  <Switch
                    checked={localSettings.autoGhostEnabled}
                    onChange={(next) => {
                      void saveNext({ ...localSettings, autoGhostEnabled: next })
                    }}
                    disabled={isSaving || isLoading}
                  />
                  <span className="text-sm text-app-muted">On</span>
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-rose-700 dark:text-rose-400">{error}</p>}
          </div>

          {/* Danger zone */}
          <div className="rounded-lg border-2 border-rose-300 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-rose-700 dark:text-rose-200">Danger zone</p>
                <p className="text-sm text-rose-700 dark:text-rose-200/80">
                  Deleting your account is permanent. Your applications and settings will be removed.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteOpen(true)}
                className="rounded-lg border-2 border-rose-600 dark:border-rose-800 bg-rose-100 dark:bg-rose-900/30 px-3 py-2 text-sm font-semibold text-rose-700 dark:text-rose-100 hover:bg-rose-200 dark:hover:bg-rose-900/50 transition-colors"
              >
                Delete account
              </button>
            </div>
          </div>
        </div>

      {/* Delete account modal */}
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-app-ink/35 dark:bg-black/60 px-4 backdrop-blur">
          <div className="card w-full max-w-lg p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase text-app-muted">Danger zone</p>
                <h3 className="text-lg font-semibold text-app-ink font-display">Delete account</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle hover:text-app-ink rounded-lg"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="border border-app-line bg-app-page/60 p-3 space-y-3 rounded-2xl shadow-app-card">
              <div>
                <label className="block text-sm font-medium text-app-ink mb-1">
                  Why are you leaving? <span className="text-rose-500">*</span>
                </label>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  disabled={isDeleting}
                  className="w-full rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-rose-500 focus:outline-none"
                >
                  <option value="">Select a reason...</option>
                  <option value="found_job">I found a job</option>
                  <option value="not_searching">No longer looking for a job</option>
                  <option value="not_useful">The app doesn't meet my needs</option>
                  <option value="missing_features">Missing features I need</option>
                  <option value="too_complicated">Too difficult to use</option>
                  <option value="too_expensive">Too expensive</option>
                  <option value="rarely_use">I rarely use it</option>
                  <option value="temporary">I only needed it temporarily</option>
                  <option value="switching">Switching to another app</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-app-ink mb-1">
                  Anything else you'd like to share? <span className="text-xs text-app-muted">(optional)</span>
                </label>
                <textarea
                  value={deleteDetails}
                  onChange={(e) => setDeleteDetails(e.target.value)}
                  disabled={isDeleting}
                  rows={2}
                  maxLength={500}
                  placeholder="Tell us more..."
                  className="w-full rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-rose-500 focus:outline-none resize-none"
                />
              </div>

              <hr className="border-app-line" />

              <p className="text-sm text-app-ink">
                To confirm, type your email address. This action cannot be undone.
              </p>
              <p className="text-xs text-app-muted">
                Signed in as: <span className="font-semibold text-app-ink">{expectedEmail ?? '—'}</span>
              </p>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-rose-500 focus:outline-none"
                disabled={isDeleting}
              />
              {!expectedEmail && (
                <p className="text-xs text-rose-700 dark:text-rose-200">Email is unavailable. Refresh and try again.</p>
              )}
              {expectedEmail && confirmEmail.trim() && !canDelete && (
                <p className="text-xs text-rose-700 dark:text-rose-200">Email does not match.</p>
              )}
              {deleteError && <p className="text-sm text-rose-700 dark:text-rose-200">{deleteError}</p>}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsDeleteOpen(false)}
                disabled={isDeleting}
                className="rounded-lg border border-app-line px-3 py-2 text-sm font-semibold text-app-ink hover:bg-app-subtle disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!canDelete}
                onClick={async () => {
                  setDeleteError(null)
                  setIsDeleting(true)
                  try {
                    await Promise.resolve(onDeleteAccount(confirmEmail.trim(), deleteReason, deleteDetails.trim() || undefined))
                  } catch (err) {
                    console.error(err)
                    const msg =
                      err instanceof Error
                        ? err.message
                        : typeof err === 'string'
                          ? err
                          : 'Failed to delete account.'
                    setDeleteError(msg)
                    setIsDeleting(false)
                    return
                  }
                }}
                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
              >
                {isDeleting ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
